import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Weight } from 'careit';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as FoodTypesIcons from 'src/components/icons/foodTypes';
import { parseJsonArray } from 'src/utils/json';

const EPSILON = 1e-9;

const toLbs = (value, fromUnit) =>
  new Weight(Number(value) || 0, fromUnit || 'lbs').as('lbs');

const fromLbs = (valueLbs, toUnit) =>
  new Weight(Number(valueLbs) || 0, 'lbs').asRounded(toUnit || 'lbs');

const formatAmount = (value) => {
  const number = Number(value) || 0;
  return Number.isInteger(number) ? number : Number(number.toFixed(2));
};

const getRowKey = (row = {}) =>
  row.food_type_id || row.foodTypeId || row.food_type || row.rescue_food_type;

const foodTypeIcons = {
  'animal-feed': FoodTypesIcons.AnimalFeedIcon,
  baked: FoodTypesIcons.BakeryIcon,
  beverages: FoodTypesIcons.BeveragesIcon,
  compost: FoodTypesIcons.CompostIcon,
  dairy: FoodTypesIcons.DairyIcon,
  frozen: FoodTypesIcons.FrozenIcon,
  meat: FoodTypesIcons.MeatIcon,
  'non-food': FoodTypesIcons.NonFoodIcon,
  'non-perishable': FoodTypesIcons.NonPerishableIcon,
  prepared: FoodTypesIcons.PreparedIcon,
  produce: FoodTypesIcons.ProduceIcon,
};

const normalizeFoodType = (foodType = '') =>
  String(foodType).toLowerCase().replaceAll('_', '-');

const mergeClaimRows = ({ unassigned, existingClaims }) => {
  const rowsByKey = new Map();

  const ensureRow = (row) => {
    const key = getRowKey(row);
    if (!key) return null;

    if (!rowsByKey.has(key)) {
      rowsByKey.set(key, {
        food_type_id: row.food_type_id || row.foodTypeId,
        food_type: row.food_type || row.rescue_food_type,
        weight_unit: row.weight_unit || 'lbs',
        min_reservable_weight: row.min_reservable_weight,
        max_reservable_weight: row.max_reservable_weight,
        available_weight_lbs: 0,
        current_weight_lbs: 0,
      });
    }

    const merged = rowsByKey.get(key);
    merged.food_type_id =
      merged.food_type_id || row.food_type_id || row.foodTypeId;
    merged.food_type =
      merged.food_type || row.food_type || row.rescue_food_type;
    merged.weight_unit = merged.weight_unit || row.weight_unit || 'lbs';
    merged.min_reservable_weight =
      merged.min_reservable_weight ?? row.min_reservable_weight;
    merged.max_reservable_weight =
      merged.max_reservable_weight ?? row.max_reservable_weight;
    return merged;
  };

  unassigned.forEach((row) => {
    const merged = ensureRow(row);
    if (!merged) return;
    merged.available_weight_lbs += Number(row.partial_weight_reserved) || 0;
    merged.weight_unit = row.weight_unit || merged.weight_unit;
    merged.min_reservable_weight =
      row.min_reservable_weight ?? merged.min_reservable_weight;
    merged.max_reservable_weight =
      row.max_reservable_weight ?? merged.max_reservable_weight;
  });

  existingClaims.forEach((row) => {
    const merged = ensureRow(row);
    if (!merged) return;
    merged.current_weight_lbs += Number(row.partial_weight_reserved) || 0;
    merged.weight_unit = row.weight_unit || merged.weight_unit;
  });

  return Array.from(rowsByKey.values()).filter(
    (row) => row.available_weight_lbs > 0 || row.current_weight_lbs > 0
  );
};

const getMaxLbs = (row) => {
  const adjustable = row.available_weight_lbs + row.current_weight_lbs;
  const donorMax = Number(row.max_reservable_weight);

  if (Number.isFinite(donorMax) && donorMax > 0) {
    return Math.max(row.current_weight_lbs, Math.min(donorMax, adjustable));
  }

  return adjustable;
};

const buildDefaultClaims = ({ rows, isEditing }) =>
  rows.map((row) => {
    const defaultLbs = isEditing ? row.current_weight_lbs : getMaxLbs(row);
    return String(fromLbs(defaultLbs, row.weight_unit));
  });

const DialogClaimPartOfPartialRescue = ({
  donation,
  existingRescue,
  onClaim = () => {},
  open,
  close,
}) => {
  const { t } = useTranslation('translation');

  const freshUnassignedFoodTypes = existingRescue?.unassigned_food_types;
  const unassigned = useMemo(
    () =>
      parseJsonArray(
        existingRescue
          ? freshUnassignedFoodTypes
          : donation?.unassigned_food_types
      ),
    [donation?.unassigned_food_types, existingRescue, freshUnassignedFoodTypes]
  );
  const existingClaims = useMemo(
    () => parseJsonArray(existingRescue?.partial_food_types),
    [existingRescue]
  );
  const isEditing = existingClaims.length > 0;
  const rows = useMemo(
    () => mergeClaimRows({ unassigned, existingClaims }),
    [unassigned, existingClaims]
  );

  const [claims, setClaims] = useState(() =>
    buildDefaultClaims({ rows, isEditing })
  );
  const [errors, setErrors] = useState(() => rows.map(() => null));

  useEffect(() => {
    setClaims(buildDefaultClaims({ rows, isEditing }));
    setErrors(rows.map(() => null));
  }, [rows, isEditing]);

  const validateRow = (rowIdx, displayValue) => {
    const row = rows[rowIdx];
    if (!row || displayValue === '' || displayValue == null) return null;

    const num = Number(displayValue);
    if (!Number.isFinite(num) || num < 0) {
      return t('form.validation.required');
    }
    if (num === 0) return null;

    const unit = row.weight_unit || 'lbs';
    const maxDisplay = fromLbs(getMaxLbs(row), unit);
    const minLbs = Number(row.min_reservable_weight) || 0;
    const minDisplay = minLbs > 0 ? fromLbs(minLbs, unit) : 0;
    const currentDisplay = fromLbs(row.current_weight_lbs, unit);

    if (num > maxDisplay + EPSILON) {
      return t('donations.pickup-maximum-error', {
        amount: formatAmount(maxDisplay),
        unit,
      });
    }
    if (
      minDisplay > 0 &&
      num < minDisplay - EPSILON &&
      Math.abs(num - currentDisplay) > EPSILON
    ) {
      return t('donations.pickup-minimum-error', {
        amount: formatAmount(minDisplay),
        unit,
      });
    }
    return null;
  };

  const handleChange = (rowIdx, value) => {
    setClaims((prev) => {
      const next = [...prev];
      next[rowIdx] = value;
      return next;
    });
    setErrors((prev) => {
      const next = [...prev];
      next[rowIdx] = validateRow(rowIdx, value);
      return next;
    });
  };

  const toClaimWeightLbs = useCallback((row, displayValue) => {
    const unit = row.weight_unit || 'lbs';
    const num = Number(displayValue);
    const maxLbs = getMaxLbs(row);
    const maxDisplay = fromLbs(maxLbs, unit);
    const currentDisplay = fromLbs(row.current_weight_lbs, unit);

    if (Math.abs(num - maxDisplay) < EPSILON) return maxLbs;
    if (Math.abs(num - currentDisplay) < EPSILON) {
      return row.current_weight_lbs;
    }
    return toLbs(num, unit);
  }, []);

  const partialClaims = useMemo(
    () =>
      rows
        .map((row, idx) => {
          const num = Number(claims[idx]);
          if (!Number.isFinite(num) || num < 0) return null;
          if (!isEditing && num <= 0) return null;

          return {
            foodTypeId: row.food_type_id,
            weight: toClaimWeightLbs(row, claims[idx]),
          };
        })
        .filter(Boolean),
    [claims, isEditing, rows, toClaimWeightLbs]
  );

  const totalClaimLbs = partialClaims.reduce(
    (total, claim) => total + (Number(claim.weight) || 0),
    0
  );
  const totalAvailableToUserLbs = rows.reduce(
    (total, row) => total + row.available_weight_lbs + row.current_weight_lbs,
    0
  );
  const leftOpenLbs = Math.max(totalAvailableToUserLbs - totalClaimLbs, 0);
  const summaryUnit = rows[0]?.weight_unit || 'lbs';
  const hasErrors = errors.some(Boolean);
  const submitDisabled =
    hasErrors || partialClaims.length === 0 || totalClaimLbs <= 0;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submitDisabled) return;
    onClaim({
      partialClaims,
      replaceExistingPartialClaims: isEditing,
    });
    close();
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={close}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1.25 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h6">
                {isEditing
                  ? t('donations.partial-claim-edit-title')
                  : t('donations.partial-claim-title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {isEditing
                  ? t('donations.partial-claim-edit-description')
                  : t('donations.partial-claim-description')}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('common.close')}
              onClick={close}
              sx={{ flexShrink: 0, mt: -0.5 }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={1.75}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.25, sm: 2 }}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.25,
                bgcolor: 'primary.lighter',
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                {t('donations.partial-claim-you-claim')}{' '}
                {formatAmount(fromLbs(totalClaimLbs, summaryUnit))}{' '}
                {summaryUnit}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('donations.partial-claim-left-open')}{' '}
                {formatAmount(fromLbs(leftOpenLbs, summaryUnit))} {summaryUnit}
              </Typography>
            </Stack>

            <Stack spacing={1.25}>
              {rows.map((row, idx) => {
                const unit = row.weight_unit || 'lbs';
                const FoodTypeIcon =
                  foodTypeIcons[normalizeFoodType(row.food_type)];
                const maxDisplay = fromLbs(getMaxLbs(row), unit);
                const currentValue = Number(claims[idx]);
                const sliderValue = Number.isFinite(currentValue)
                  ? Math.min(Math.max(currentValue, 0), maxDisplay)
                  : 0;
                const currentDisplay = fromLbs(row.current_weight_lbs, unit);
                const availableToUserDisplay = fromLbs(
                  row.available_weight_lbs + row.current_weight_lbs,
                  unit
                );
                const minDisplay =
                  Number(row.min_reservable_weight) > 0
                    ? fromLbs(row.min_reservable_weight, unit)
                    : null;
                const hasMaxLimit = maxDisplay < availableToUserDisplay;
                let limitText = '';
                if (minDisplay != null) {
                  limitText = t('donations.partial-claim-donor-limit-range', {
                    min: formatAmount(minDisplay),
                    max: formatAmount(maxDisplay),
                    unit,
                  });
                } else if (hasMaxLimit) {
                  limitText = t('donations.partial-claim-donor-limit', {
                    amount: formatAmount(maxDisplay),
                    unit,
                  });
                }

                return (
                  <Box
                    key={row.food_type_id || row.food_type || idx}
                    sx={{
                      py: 1.25,
                      borderBottom: (theme) =>
                        idx === rows.length - 1
                          ? 'none'
                          : `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.25}
                      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        {FoodTypeIcon ? (
                          <Box
                            sx={{
                              alignItems: 'center',
                              bgcolor: 'primary.lighter',
                              borderRadius: 1,
                              color: 'primary.main',
                              display: 'flex',
                              flexShrink: 0,
                              height: 32,
                              justifyContent: 'center',
                              width: 32,
                            }}
                          >
                            <FoodTypeIcon sx={{ fontSize: 20 }} />
                          </Box>
                        ) : null}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2">
                            {row.food_type}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                          >
                            {t('donations.partial-claim-available-to-you', {
                              amount: formatAmount(availableToUserDisplay),
                              unit,
                            })}
                            {isEditing
                              ? ` · ${t(
                                  'donations.partial-claim-current-claim',
                                  {
                                    amount: formatAmount(currentDisplay),
                                    unit,
                                  }
                                )}`
                              : ''}
                            {limitText ? ` · ${limitText}` : ''}
                          </Typography>
                        </Box>
                      </Stack>
                      <TextField
                        size="small"
                        type="number"
                        label={t('donations.partial-claim-amount-label')}
                        value={claims[idx]}
                        onChange={(event) =>
                          handleChange(idx, event.target.value)
                        }
                        onKeyDown={(event) => {
                          if (event.key === '-' || event.key === 'e') {
                            event.preventDefault();
                          }
                        }}
                        error={!!errors[idx]}
                        helperText={errors[idx] || ' '}
                        inputProps={{ min: 0, max: maxDisplay, step: 'any' }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {unit}
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          width: { xs: '100%', sm: 148 },
                          '& .MuiFormHelperText-root': { mx: 0 },
                        }}
                      />
                    </Stack>

                    <Slider
                      size="small"
                      value={sliderValue}
                      min={0}
                      max={maxDisplay}
                      step={unit === 'lbs' ? 1 : 0.1}
                      onChange={(_, value) => handleChange(idx, String(value))}
                      aria-label={`${row.food_type} ${t(
                        'donations.partial-claim-amount-label'
                      )}`}
                      sx={{ mt: 0.25 }}
                    />
                  </Box>
                );
              })}
            </Stack>

            {submitDisabled && !hasErrors ? (
              <Typography variant="caption" color="text.secondary">
                {t('donations.partial-claim-none-selected')}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" disabled={submitDisabled}>
            {isEditing
              ? t('donations.partial-claim-update')
              : t('donations.claim-donation')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default DialogClaimPartOfPartialRescue;
